/**
 * Funciones puras para calcular bandas a partir de datos FFT crudos.
 * Ver docs/SPEC-IMPLEMENTACION.md §5.3.
 */

import type { BandasAudio } from './tipos';

/** Rango de bandas para cada grupo (índices en el array de 128 bandas). */
export const RANGO_BANDAS = {
  bajo: { inicio: 0, fin: 10 },
  medio: { inicio: 10, fin: 50 },
  agudo: { inicio: 50, fin: 128 },
} as const;

/**
 * Calcula el promedio normalizado (0–1) de un rango de bandas.
 */
function promedio(datos: Uint8Array, inicio: number, fin: number): number {
  let suma = 0;
  let contador = 0;
  for (let i = inicio; i < fin && i < datos.length; i++) {
    suma += datos[i] ?? 0;
    contador++;
  }
  if (contador === 0) return 0;
  return (suma / contador) / 255;
}

/**
 * Convierte el array de frecuencias crudas en bandas agrupadas.
 */
export function calcularBandas(datos: Uint8Array): BandasAudio {
  return {
    bajo: promedio(datos, RANGO_BANDAS.bajo.inicio, RANGO_BANDAS.bajo.fin),
    medio: promedio(datos, RANGO_BANDAS.medio.inicio, RANGO_BANDAS.medio.fin),
    agudo: promedio(datos, RANGO_BANDAS.agudo.inicio, RANGO_BANDAS.agudo.fin),
    global: promedio(datos, 0, datos.length),
  };
}

/**
 * Suavizado exponencial reactivo.
 * Factor 0.45 permite captar los golpes rítmicos y agudos de inmediato sin latencia perceptible.
 */
export function suavizar(actual: BandasAudio, nuevo: BandasAudio, factor = 0.45): BandasAudio {
  return {
    bajo: actual.bajo + (nuevo.bajo - actual.bajo) * factor,
    medio: actual.medio + (nuevo.medio - actual.medio) * factor,
    agudo: actual.agudo + (nuevo.agudo - actual.agudo) * factor,
    global: actual.global + (nuevo.global - actual.global) * factor,
  };
}
